<template>
  <div class="global-container">
    <div class="content">
      <!-- Header Section -->
      <div class="header-section">
        <div class="title-section">
          <h1 class="page-title">Chart of Accounts</h1>
          <p class="page-description">Manage your account structure and journal entries</p>
        </div>
        <button class="new-voucher-button" @click="openVoucherModal">
          <span class="plus-icon">+</span>
          New Journal Voucher
        </button>
      </div>

      <!-- Stats Cards -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Total Accounts</div>
          <div class="stat-value">{{ totalAccounts }}</div>
          <div class="stat-subtitle">Across all categories</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Journal Vouchers</div>
          <div class="stat-value">{{ journalVouchers }}</div>
          <div class="stat-subtitle">Posted entries</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Asset Accounts</div>
          <div class="stat-value">{{ assetAccounts }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Expense Accounts</div>
          <div class="stat-value">{{ expenseAccounts }}</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="tabs-section">
        <div class="tabs">
          <button
            class="tab"
            :class="{ active: activeTab === 'tree' }"
            @click="activeTab = 'tree'"
          >
            Account Tree
          </button>
          <button
            class="tab"
            :class="{ active: activeTab === 'vouchers' }"
            @click="activeTab = 'vouchers'"
          >
            Journal Vouchers
          </button>
        </div>
      </div>

      <!-- Search Bar -->
      <div class="search-section">
        <input
          type="text"
          placeholder="Search accounts..."
          class="search-input"
          v-model="searchQuery"
        />
      </div>

      <!-- Account Tree Component -->
      <div v-if="activeTab === 'tree'" class="tree-section">
        <AccountTree :accounts="accounts" :onAdd="openAddAccountModal" :onEdit="openEditAccountModal" :onDelete="confirmDeleteAccount" />
      </div>

      <!-- Journal Vouchers Section -->
      <div v-else class="vouchers-section">
        <JournalVouchersTable />
      </div>
     <NewJournalVoucherModal
        :show="showVoucherModal"
        :accounts="flatAccounts"
        @close="showVoucherModal = false"
      />
      <AddChartOfAccountModal
        :isOpen="showAddAccountModal"
        :parentAccount="addAccountParent"
        :editAccount="editAccountData"
        @close="showAddAccountModal = false"
        @added="showAddAccountModal = false"
        @updated="showAddAccountModal = false"
      />

      <!-- Delete Confirmation -->
      <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
        <div class="confirm-modal">
          <h3>Delete Account</h3>
          <p>Are you sure you want to delete "<strong>{{ deleteAccountData && deleteAccountData.name }}</strong>"?</p>
          <p v-if="deleteAccountData && deleteAccountData.children && deleteAccountData.children.length" class="warning-text">This account has sub-accounts that may also be affected.</p>
          <div class="confirm-actions">
            <button class="btn-cancel-confirm" @click="showDeleteConfirm = false">Cancel</button>
            <button class="btn-delete-confirm" @click="handleDeleteAccount" :disabled="deleting">
              {{ deleting ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
      </div>
  </div>
</template>

<script>
import AccountTree from '@/components/AccountTree.vue'
import NewJournalVoucherModal from '@/components/NewJournalVoucherModal.vue'
import AddChartOfAccountModal from '@/components/AddChartOfAccountModal.vue'
import { mapActions, mapState } from 'vuex'
import JournalVouchersTable from '@/components/JournalVouchersTable.vue'

export default {
  name: 'ChartOfAccounts',
  components: {
    AccountTree,
    NewJournalVoucherModal,
    AddChartOfAccountModal,
    JournalVouchersTable
  },
  data() {
    return {
      activeTab: 'tree',
      searchQuery: '',
      isModalOpen: false,
      showVoucherModal: false,
      showAddAccountModal: false,
      addAccountParent: null,
      editAccountData: null,
      showDeleteConfirm: false,
      deleteAccountData: null,
      deleting: false
    }
    },
    methods: {
        ...mapActions('accounting', ['fetchChartOfAccounts', 'deleteChartOfAccount']),
     flattenAccounts(list) {
  let result = []

  list.forEach(item => {
    if (item.type === 'account') {
      result.push(item)
    }

    if (item.children && item.children.length) {
      result = result.concat(this.flattenAccounts(item.children))
    }
  })

  return result
      },
    openModal() {
      this.isModalOpen = true
    },
    closeModal() {
      this.isModalOpen = false
    },
    handleCreateVoucher(voucherData) {
      console.log('Creating journal voucher:', voucherData)
      // Add your API call here to save the voucher
      // this.$store.dispatch('accounting/createJournalVoucher', voucherData)
      },
    openVoucherModal() {
      this.showVoucherModal = true
    },
    openAddAccountModal(parentAccount) {
      this.addAccountParent = parentAccount
      this.editAccountData = null
      this.showAddAccountModal = true
    },
    openEditAccountModal(account) {
      this.editAccountData = account
      this.addAccountParent = null
      this.showAddAccountModal = true
    },
    confirmDeleteAccount(account) {
      this.deleteAccountData = account
      this.showDeleteConfirm = true
    },
    async handleDeleteAccount() {
      if (!this.deleteAccountData) return
      this.deleting = true
      try {
        await this.deleteChartOfAccount(this.deleteAccountData.id)
        this.showDeleteConfirm = false
        this.deleteAccountData = null
      } catch (e) {
        // error handled in store
      } finally {
        this.deleting = false
      }
    }
    },
    computed: {
        ...mapState('accounting', ['chartOfAccounts']),
        accounts() {
      return this.chartOfAccounts
        },
  flatAccounts() {
    return this.flattenAccounts(this.chartOfAccounts)
  },

  totalAccounts() {
    return this.flatAccounts.length
  },

  assetAccounts() {
    return this.flatAccounts.filter(a => a.category === 'Asset').length
  },

  expenseAccounts() {
    return this.flatAccounts.filter(a => a.category === 'Expense').length
  },

  journalVouchers() {
    // jab backend ready ho — Vuex se mapState
    return 0
      }
    },
  created() {
    this.fetchChartOfAccounts()
  }
}
</script>

<style scoped>
/* Header Section */
.header-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 32px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.page-description {
  font-size: 15px;
  color: #9ca3af;
  font-weight: 400;
}

/* New Voucher Button */
.new-voucher-button {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s ease;
  white-space: nowrap;
}

.new-voucher-button:hover {
  background: #1d4ed8;
}

.plus-icon {
  font-size: 18px;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
}

.stat-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 600;
  margin-bottom: 12px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 4px;
}

.stat-subtitle {
  font-size: 12px;
  color: #9ca3af;
}

/* Tabs Section */
.tabs-section {
  margin-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.tabs {
  display: flex;
  gap: 24px;
}

.tab {
  background: none;
  border: none;
  padding: 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  position: relative;
  transition: color 0.2s ease;
}

.tab:hover {
  color: #1f2937;
}

.tab.active {
  color: #2563eb;
}

.tab.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: #2563eb;
}

/* Search Section */
.search-section {
  margin-bottom: 24px;
}

.search-input {
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #2563eb;
}

.search-input::placeholder {
  color: #9ca3af;
}

/* Tree and Vouchers Sections */
.tree-section,
.vouchers-section {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

/* Delete Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1002;
}

.confirm-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.confirm-modal h3 { font-size: 18px; font-weight: 700; color: #1f2937; margin-bottom: 12px; }
.confirm-modal p { color: #6b7280; margin-bottom: 16px; font-size: 14px; }
.warning-text { color: #d97706; font-size: 13px; }
.confirm-actions { display: flex; gap: 12px; justify-content: flex-end; }

.btn-cancel-confirm {
  padding: 10px 24px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #1f2937;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel-confirm:hover { background: #f3f4f6; }

.btn-delete-confirm {
  padding: 10px 24px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-delete-confirm:hover { background: #dc2626; }
.btn-delete-confirm:disabled { opacity: 0.7; cursor: not-allowed; }

@media (max-width: 768px) {

  .header-section {
    flex-direction: column;
    gap: 24px;
  }

  .page-title {
    font-size: 28px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .tabs {
    gap: 16px;
  }
}

</style>
